import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../components/table/coreUiTable/Table";
import LoadingAnimation from "../../components/loadingAnimation/loadingAnimation";
import Filter from "./Filter";
import InfoTooltip from "./InfoTooltip";
import Overlay from "../../components/globalOverlay/globalOverlay";

// returns array of indexes in form of [_1, _2, ..., _len]
const getColumns = (len) => {
  return [...Array(len - 2).keys()].map((key) => `_${key + 1}`);
};

const Heatmap = ({
  params: { organization_id, short_url },
  includeCrossRegionalVisits,
  fetchHeatmapData,
  sourceType,
  membershipLogic,
  stateName = null
}) => {
  const dispatch = useDispatch();
  const {
    isLoading,
    data,
    links,
    month,
    riders,
    filters,
    regionalVisitOptions,
    selectedRegionalVisit,
    numbersOrPercentageOptions
  } = useSelector((state) => (stateName ? state[stateName] : state));
  const checked = filters?.numbersOrPercentages === "numbers";
  const [regionalVisit, setRegionalVisit] = useState(
    includeCrossRegionalVisits ? selectedRegionalVisit : {}
  );
  // fetching data
  useEffect(() => {
    dispatch(
      fetchHeatmapData({
        organization_id,
        short_url,
        includeCrossRegionalVisits,
        sourceType,
        membershipLogic
      })
    );
  }, [organization_id, short_url]);

  // return modified td to inject into each cell
  const getTd = (item, index, col) => {
    let value;

    if (typeof item === "string" && item.endsWith("K")) {
      const replacedValue = item.replace("K", "");
      value = replacedValue * 1000;
    } else {
      value = item;
    }

    return (
      <td
        style={{
          display: item < 0 ? "none" : "",
          backgroundColor:
            index > 1 ? (value >= 0 ? `rgba(41, 112, 172, ${value / 100})` : "#272a32") : ""
        }}
      >
        <div>
          {index > 0
            ? value >= 0 &&
              (index > 1 ? (
                <a
                  target="_blank"
                  href={links[index - 2][col - 1]}
                  className="heatmap-link"
                  rel="noreferrer"
                >
                  {`${item}${checked ? "" : "%"}`}
                </a>
              ) : (
                `${item}${checked ? "" : "%"}`
              ))
            : item}
        </div>
      </td>
    );
  };

  // returns object of objects to override each _n column in form of {_n: (item, index) => getTd}
  const getSlots = (firstRow) => {
    return Object.assign(
      {},
      ...firstRow.slice(2, firstRow.length).map((col) => {
        return { [`_${col}`]: (item, index) => getTd(item[`_${col}`], index, col) };
      })
    );
  };

  const update = ({
    numbersOrPercentages = filters.numbersOrPercentages,
    classPass = filters.classPass,
    includeCrossRegionalVisits = regionalVisit
  }) => {
    dispatch(
      fetchHeatmapData({
        organization_id,
        short_url,
        classPass: classPass,
        numbersOrPercentages: numbersOrPercentages,
        includeCrossRegionalVisits: includeCrossRegionalVisits
      })
    );
  };

  const handleRegionalVisits = (data) => {
    setRegionalVisit(data);
    update({ includeCrossRegionalVisits: data });
  };

  return data.length > 0 ? (
    <>
      <Overlay
        isVisible={isLoading}
        portalId="#react-heatmap-overlay"
        style={{ position: "absolute", height: "100%", width: "100%" }}
      >
        <LoadingAnimation />
      </Overlay>
      {/*categories can be missing*/}
      {filters && filters.categories && filters.categories.length > 0 && (
        <div
          className="heatmap-filters"
          style={{ padding: "0 0 1rem 0", width: "11%", display: "inline-flex" }}
        >
          <Filter
            keys={{ label: "label" }}
            options={filters?.categories}
            selectedFilters={filters?.categories.filter((category) => category.checked === true)}
            onChange={(values) => {
              dispatch(
                fetchHeatmapData({
                  categories: values
                })
              );
            }}
            numberDisplayed={0}
            manySelectedPlaceholder="Sales Packages"
            allSelectedPlaceholder="Sales Packages"
          />
        </div>
      )}
      <div
        className="heatmap-filters"
        style={{ display: "inline-flex", gap: "15px", marginBottom: "10px" }}
      >
        {includeCrossRegionalVisits && (
          <div className="select-box-no-width" style={{ width: "245px" }}>
            <Filter
              isMulti={false}
              keys={{ label: "label", value: "value" }}
              options={regionalVisitOptions}
              selectedFilters={regionalVisit}
              onChange={(data) => {
                handleRegionalVisits(data);
              }}
            />
          </div>
        )}
        {filters && filters.classPasses && (
          <>
            <div className="select-box-no-width" style={{ width: "200px" }}>
              <Filter
                isMulti={false}
                keys={{ label: "label", value: "value" }}
                options={filters?.classPasses}
                selectedFilters={filters.classPasses.find(
                  ({ value }) => value === filters?.classPass
                )}
                onChange={({ value }) => update({ classPass: value })}
              />
            </div>
            <div className="select-box-no-width" style={{ width: "170px" }}>
              <Filter
                isMulti={false}
                keys={{ label: "label", value: "value" }}
                options={numbersOrPercentageOptions}
                selectedFilters={numbersOrPercentageOptions.find(
                  ({ value }) => value === filters?.numbersOrPercentages
                )}
                onChange={({ value }) => update({ numbersOrPercentages: value })}
              />
            </div>
          </>
        )}
      </div>
      <Table
        table={{
          colHeaders: ["month", "riders", ...getColumns(data[0].length)],
          columns: [],
          data
        }}
        overrideColumnCallback={(header, index) => ({
          label:
            index === 2 ? (
              <div className="month-info">
                <InfoTooltip
                  tooltipId="month-info-tooltip"
                  style={{ color: "#337ab7", paddingRight: "5px" }}
                  title={month.tooltip}
                />
                {month.text}
              </div>
            ) : (
              ""
            )
        })}
        scopedSlots={{
          month: (item) => <td className="month">{item.month}</td>,
          riders: (item, index) => (
            <td className="riders">
              {index === 0 && riders.text ? (
                <>
                  <div className="riders-label">{riders.text}</div>
                  <InfoTooltip
                    tooltipId="riders-tooltip"
                    style={{ color: "#337ab7" }}
                    title={riders.tooltip}
                  />
                </>
              ) : (
                item.riders
              )}
            </td>
          ),
          ...getSlots(data[0])
        }}
        tableFilter={false}
        pagination={false}
        tableWrapperId="heatmap"
      />
    </>
  ) : (
    <LoadingAnimation />
  );
};

Heatmap.propTypes = {
  locationId: PropTypes.number,
  objectId: PropTypes.number,
  regionId: PropTypes.number,
  organizationId: PropTypes.number,
  fetchHeatmapData: PropTypes.func.isRequired,
  sourceType: PropTypes.string
};

export default Heatmap;
